import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import PropTypes from 'prop-types';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $createRangeSelection,
  $getNodeByKey,
  $getRoot,
  $isTextNode,
  $setSelection,
} from 'lexical';

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const countOccurrences = (text, query, caseSensitive) => {
  if (!query) {
    return 0;
  }
  if (caseSensitive) {
    return text.split(query).length - 1;
  }
  const matcher = new RegExp(escapeRegExp(query), 'gi');
  const matches = text.match(matcher);
  return matches ? matches.length : 0;
};

const collectMatches = (editorState, query, caseSensitive) => {
  if (!query) {
    return [];
  }

  const matches = [];
  editorState.read(() => {
    const root = $getRoot();
    const textNodes = root.getAllTextNodes();
    const needle = caseSensitive ? query : query.toLowerCase();

    textNodes.forEach((node) => {
      const text = node.getTextContent();
      if (!text) {
        return;
      }
      const haystack = caseSensitive ? text : text.toLowerCase();
      let cursor = 0;
      while (cursor <= haystack.length - needle.length) {
        const index = haystack.indexOf(needle, cursor);
        if (index === -1) {
          break;
        }
        matches.push({
          key: node.getKey(),
          start: index,
          end: index + query.length,
        });
        cursor = index + Math.max(1, query.length);
      }
    });
  });

  return matches;
};

const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

const isSameMatch = (a, b) => (
  a?.key === b?.key
  && a?.start === b?.start
  && a?.end === b?.end
);

export const FindReplacePlugin = forwardRef(function FindReplacePlugin(
  {
    active,
    query,
    caseSensitive,
    onStateChange,
  },
  ref
) {
  const [editor] = useLexicalComposerContext();
  const matchesRef = useRef([]);
  const currentIndexRef = useRef(0);
  const onStateChangeRef = useRef(onStateChange);
  const lastEmittedStateRef = useRef({
    matchCount: null,
    currentMatch: null,
    replacedCount: null,
  });
  const lastFocusedMatchRef = useRef(null);

  useEffect(() => {
    onStateChangeRef.current = onStateChange;
  }, [onStateChange]);

  const emitState = useCallback((extra = {}) => {
    const stateHandler = onStateChangeRef.current;
    if (typeof stateHandler !== 'function') {
      return;
    }
    const count = matchesRef.current.length;
    const index = count > 0 ? Math.min(currentIndexRef.current + 1, count) : 0;
    const replacedCount = hasOwn(extra, 'replacedCount') ? extra.replacedCount : null;
    const last = lastEmittedStateRef.current;
    if (
      last.matchCount === count
      && last.currentMatch === index
      && last.replacedCount === replacedCount
    ) {
      return;
    }
    lastEmittedStateRef.current = {
      matchCount: count,
      currentMatch: index,
      replacedCount,
    };

    const nextState = {
      matchCount: count,
      currentMatch: index,
    };
    Object.entries(extra).forEach(([key, value]) => {
      nextState[key] = value;
    });
    stateHandler(nextState);
  }, []);

  const focusMatch = useCallback((match, behavior = 'smooth', options = {}) => {
    if (!match) {
      return;
    }
    const { force = false } = options;
    if (!force && isSameMatch(lastFocusedMatchRef.current, match)) {
      return;
    }
    lastFocusedMatchRef.current = {
      key: match.key,
      start: match.start,
      end: match.end,
    };

    editor.update(() => {
      const node = $getNodeByKey(match.key);
      if (!$isTextNode(node)) {
        return;
      }
      const maxOffset = node.getTextContentSize();
      const start = Math.max(0, Math.min(match.start, maxOffset));
      const end = Math.max(start, Math.min(match.end, maxOffset));
      const selection = $createRangeSelection();
      selection.anchor.set(node.getKey(), start, 'text');
      selection.focus.set(node.getKey(), end, 'text');
      $setSelection(selection);
    });

    if (typeof window !== 'undefined') {
      window.requestAnimationFrame(() => {
        const element = editor.getElementByKey(match.key);
        if (element) {
          element.scrollIntoView({ behavior, block: 'center' });
        }
      });
    }
  }, [editor]);

  const refreshMatches = useCallback((options = {}) => {
    const {
      focus = false,
      behavior = 'auto',
      ...rest
    } = options;
    const nextMatches = collectMatches(editor.getEditorState(), query, caseSensitive);
    matchesRef.current = nextMatches;
    if (nextMatches.length === 0) {
      currentIndexRef.current = 0;
      lastFocusedMatchRef.current = null;
      emitState(rest);
      return;
    }

    currentIndexRef.current = Math.min(currentIndexRef.current, nextMatches.length - 1);
    if (active && focus) {
      focusMatch(nextMatches[currentIndexRef.current], behavior);
    }
    emitState(rest);
  }, [active, caseSensitive, editor, emitState, focusMatch, query]);

  useEffect(() => {
    return editor.registerUpdateListener(() => {
      refreshMatches();
    });
  }, [editor, refreshMatches]);

  useEffect(() => {
    refreshMatches({
      focus: active && Boolean(query),
      behavior: 'auto',
      replacedCount: 0,
    });
  }, [query, caseSensitive, active, refreshMatches]);

  const goToIndex = useCallback((index) => {
    const matches = matchesRef.current;
    if (!matches.length) {
      currentIndexRef.current = 0;
      emitState();
      return;
    }
    const safeIndex = ((index % matches.length) + matches.length) % matches.length;
    currentIndexRef.current = safeIndex;
    focusMatch(matches[safeIndex], 'smooth', { force: true });
    emitState();
  }, [emitState, focusMatch]);

  const replaceCurrent = useCallback((replaceValue) => {
    const matches = matchesRef.current;
    if (!matches.length) {
      return 0;
    }
    const current = matches[currentIndexRef.current];
    editor.update(() => {
      const node = $getNodeByKey(current.key);
      if (!$isTextNode(node)) {
        return;
      }
      const text = node.getTextContent();
      const nextText = `${text.slice(0, current.start)}${replaceValue}${text.slice(current.end)}`;
      node.setTextContent(nextText);
    });
    refreshMatches({
      focus: active && Boolean(query),
      replacedCount: 1,
    });
    return 1;
  }, [active, editor, query, refreshMatches]);

  const replaceAll = useCallback((replaceValue) => {
    if (!query) {
      return 0;
    }
    let total = 0;
    editor.update(() => {
      const root = $getRoot();
      root.getAllTextNodes().forEach((node) => {
        const text = node.getTextContent();
        const hits = countOccurrences(text, query, caseSensitive);
        if (!hits) {
          return;
        }
        total += hits;
        if (caseSensitive) {
          node.setTextContent(text.split(query).join(replaceValue));
        } else {
          const matcher = new RegExp(escapeRegExp(query), 'gi');
          node.setTextContent(text.replace(matcher, replaceValue));
        }
      });
    });
    refreshMatches({
      focus: active && Boolean(query),
      replacedCount: total,
    });
    return total;
  }, [active, caseSensitive, editor, query, refreshMatches]);

  useImperativeHandle(ref, () => ({
    next() {
      goToIndex(currentIndexRef.current + 1);
    },
    previous() {
      goToIndex(currentIndexRef.current - 1);
    },
    replaceCurrent,
    replaceAll,
    focusCurrent() {
      const matches = matchesRef.current;
      if (!matches.length) {
        return;
      }
      focusMatch(matches[currentIndexRef.current], 'smooth', { force: true });
    },
  }), [focusMatch, goToIndex, replaceAll, replaceCurrent]);

  return null;
});

FindReplacePlugin.propTypes = {
  active: PropTypes.bool,
  query: PropTypes.string,
  caseSensitive: PropTypes.bool,
  onStateChange: PropTypes.func,
};

FindReplacePlugin.defaultProps = {
  active: false,
  query: '',
  caseSensitive: false,
  onStateChange: null,
};
